// 物品系统 - 物品、装备、消耗品、收集品

import { EXTENDED_ITEMS } from './ExtendedItems.js';

export type ItemCategory = 'consumable' | 'equipment' | 'material' | 'collectible' | 'quest' | 'furniture';
export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';
export type EquipmentSlot = 'weapon' | 'head' | 'body' | 'legs' | 'feet' | 'accessory' | 'dandan_saddle' | 'dandan_shoes' | 'dandan_armor';

export interface ItemEffect {
  type: 'heal' | 'buff' | 'restore' | 'permanent' | 'special';
  target: 'hp' | 'stamina' | 'happiness' | 'constitution' | 'wisdom' | 'charm' | 'dandan_intimacy' | 'dandan_health';
  value: number;
  duration?: number; // 秒，0表示永久
}

export interface Item {
  id: string;
  name: string;
  description: string;
  category: ItemCategory;
  rarity: ItemRarity;
  icon: string;
  
  // 效果（消耗品）
  effects?: ItemEffect[];
  
  // 装备属性
  equipmentSlot?: EquipmentSlot;
  stats?: {
    attack?: number;
    defense?: number;
    speed?: number;
    charm?: number;
    wisdom?: number;
  };
  
  // 堆叠
  stackable: boolean;
  maxStack: number;
  
  // 价值
  sellPrice: number;
  buyPrice: number;
  
  // 使用条件
  levelReq?: number;
  flagReq?: string;
  
  // 特殊
  questItem?: boolean;
  consumable?: boolean;
}

// 背包中的物品实例
export interface InventoryItem {
  itemId: string;
  quantity: number;
  equipped?: boolean;
  durability?: number; // 装备耐久
  customName?: string; // 自定义名称
}

// 装备槽位
export interface EquipmentSlots {
  weapon?: InventoryItem;
  head?: InventoryItem;
  body?: InventoryItem;
  legs?: InventoryItem;
  feet?: InventoryItem;
  accessory?: InventoryItem;
  dandan_saddle?: InventoryItem;
  dandan_shoes?: InventoryItem;
  dandan_armor?: InventoryItem;
}

// 物品数据库
export const ITEM_DATABASE: Item[] = [
  // ========== 消耗品 - 食物 ==========
  {
    id: 'rice_ball',
    name: '饭团',
    description: '奶奶做的饭团，满满的爱意。',
    category: 'consumable',
    rarity: 'common',
    icon: '🍙',
    effects: [
      { type: 'restore', target: 'stamina', value: 20 }
    ],
    stackable: true,
    maxStack: 99,
    sellPrice: 10,
    buyPrice: 20,
    consumable: true,
  },
  {
    id: 'grandma_soup',
    name: '奶奶的菜汤',
    description: '热腾腾的汤，喝下去浑身都暖了。',
    category: 'consumable',
    rarity: 'uncommon',
    icon: '🍲',
    effects: [
      { type: 'heal', target: 'hp', value: 50 },
      { type: 'buff', target: 'constitution', value: 5, duration: 3600 }
    ],
    stackable: true,
    maxStack: 20,
    sellPrice: 50,
    buyPrice: 100,
    consumable: true,
  },
  {
    id: 'candy',
    name: '糖果',
    description: '甜甜的糖果，吃了心情会变好。',
    category: 'consumable',
    rarity: 'common',
    icon: '🍬',
    effects: [
      { type: 'buff', target: 'happiness', value: 10, duration: 1800 }
    ],
    stackable: true,
    maxStack: 99,
    sellPrice: 5,
    buyPrice: 10,
    consumable: true,
  },
  {
    id: 'dog_food',
    name: '狗粮',
    description: '专门给狗狗准备的营养食物。',
    category: 'consumable',
    rarity: 'common',
    icon: '🦴',
    effects: [
      { type: 'buff', target: 'dandan_health', value: 20, duration: 0 },
      { type: 'buff', target: 'dandan_intimacy', value: 5, duration: 0 }
    ],
    stackable: true,
    maxStack: 50,
    sellPrice: 15,
    buyPrice: 30,
    consumable: true,
  },
  
  // ========== 消耗品 - 药品 ==========
  {
    id: 'health_potion',
    name: '回血草',
    description: '普通的草药，可以恢复少量生命。',
    category: 'consumable',
    rarity: 'common',
    icon: '🌿',
    effects: [
      { type: 'heal', target: 'hp', value: 30 }
    ],
    stackable: true,
    maxStack: 99,
    sellPrice: 10,
    buyPrice: 20,
    consumable: true,
  },
  {
    id: 'stamina_potion',
    name: '回气丹',
    description: '恢复体力的丹药。',
    category: 'consumable',
    rarity: 'common',
    icon: '💊',
    effects: [
      { type: 'restore', target: 'stamina', value: 50 }
    ],
    stackable: true,
    maxStack: 99,
    sellPrice: 20,
    buyPrice: 40,
    consumable: true,
  },
  
  // ========== 装备 ==========
  {
    id: 'wooden_stick',
    name: '木棍',
    description: '一根普通的木棍，可以用来防身。',
    category: 'equipment',
    rarity: 'common',
    icon: '🦯',
    equipmentSlot: 'weapon',
    stats: { attack: 3 },
    stackable: false,
    maxStack: 1,
    sellPrice: 5,
    buyPrice: 10,
  },
  {
    id: 'straw_hat',
    name: '草帽',
    description: '遮阳用的草帽。',
    category: 'equipment',
    rarity: 'common',
    icon: '👒',
    equipmentSlot: 'head',
    stats: { defense: 1, charm: 2 },
    stackable: false,
    maxStack: 1,
    sellPrice: 15,
    buyPrice: 30,
  },
  {
    id: 'cotton_clothes',
    name: '棉布衣',
    description: '舒适的棉布衣服。',
    category: 'equipment',
    rarity: 'common',
    icon: '👕',
    equipmentSlot: 'body',
    stats: { defense: 2, charm: 1 },
    stackable: false,
    maxStack: 1,
    sellPrice: 30,
    buyPrice: 60,
  },
  {
    id: 'leather_shoes',
    name: '布鞋',
    description: '轻便的布鞋，适合走路。',
    category: 'equipment',
    rarity: 'common',
    icon: '👟',
    equipmentSlot: 'feet',
    stats: { speed: 2, defense: 1 },
    stackable: false,
    maxStack: 1,
    sellPrice: 20,
    buyPrice: 40,
  },
  {
    id: 'jade_pendant',
    name: '玉佩',
    description: '奶奶给的玉佩，据说能带来好运。',
    category: 'equipment',
    rarity: 'rare',
    icon: '📿',
    equipmentSlot: 'accessory',
    stats: { wisdom: 5, charm: 3 },
    stackable: false,
    maxStack: 1,
    sellPrice: 200,
    buyPrice: 500,
  },
  
  // ========== 丹丹装备 ==========
  {
    id: 'simple_saddle',
    name: '简易鞍具',
    description: '给丹丹用的简单鞍具。',
    category: 'equipment',
    rarity: 'common',
    icon: '🎒',
    equipmentSlot: 'dandan_saddle',
    stats: { speed: 5, defense: 2 },
    stackable: false,
    maxStack: 1,
    sellPrice: 50,
    buyPrice: 100,
  },
  {
    id: 'iron_shoes',
    name: '铁蹄铁',
    description: '保护丹丹蹄子的铁制蹄铁。',
    category: 'equipment',
    rarity: 'uncommon',
    icon: '🔩',
    equipmentSlot: 'dandan_shoes',
    stats: { speed: 3, defense: 5 },
    stackable: false,
    maxStack: 1,
    sellPrice: 80,
    buyPrice: 160,
  },
  
  // ========== 材料 ==========
  {
    id: 'wood',
    name: '木材',
    description: '普通的木材，可以用来制作各种物品。',
    category: 'material',
    rarity: 'common',
    icon: '🪵',
    stackable: true,
    maxStack: 999,
    sellPrice: 3,
    buyPrice: 6,
  },
  {
    id: 'stone',
    name: '石头',
    description: '山上捡到的石头。',
    category: 'material',
    rarity: 'common',
    icon: '🪨',
    stackable: true,
    maxStack: 999,
    sellPrice: 2,
    buyPrice: 4,
  },
  {
    id: 'herb_common',
    name: '草药',
    description: '常见的草药，有一定的药用价值。',
    category: 'material',
    rarity: 'common',
    icon: '🌱',
    stackable: true,
    maxStack: 999,
    sellPrice: 5,
    buyPrice: 10,
  },
  {
    id: 'iron_ore',
    name: '铁矿石',
    description: '含有铁的矿石，可以冶炼成铁。',
    category: 'material',
    rarity: 'uncommon',
    icon: '⛏️',
    stackable: true,
    maxStack: 999,
    sellPrice: 15,
    buyPrice: 30,
  },
  {
    id: 'vegetable',
    name: '蔬菜',
    description: '新鲜的蔬菜。',
    category: 'material',
    rarity: 'common',
    icon: '🥬',
    stackable: true,
    maxStack: 99,
    sellPrice: 5,
    buyPrice: 10,
  },
  {
    id: 'fish',
    name: '鱼',
    description: '新鲜的鱼。',
    category: 'material',
    rarity: 'common',
    icon: '🐟',
    stackable: true,
    maxStack: 99,
    sellPrice: 10,
    buyPrice: 20,
  },
  
  // ========== 收集品 ==========
  {
    id: 'pretty_stone',
    name: '漂亮石头',
    description: '一块形状奇特的石头，看起来很特别。',
    category: 'collectible',
    rarity: 'uncommon',
    icon: '💎',
    stackable: true,
    maxStack: 10,
    sellPrice: 50,
    buyPrice: 0,
  },
  {
    id: 'four_leaf_clover',
    name: '四叶草',
    description: '传说中的幸运草，很难找到。',
    category: 'collectible',
    rarity: 'rare',
    icon: '🍀',
    stackable: true,
    maxStack: 5,
    sellPrice: 100,
    buyPrice: 0,
  },
  {
    id: 'old_photo',
    name: '老照片',
    description: '一张泛黄的老照片，上面是年轻时的奶奶。',
    category: 'collectible',
    rarity: 'epic',
    icon: '📷',
    stackable: false,
    maxStack: 1,
    sellPrice: 0,
    buyPrice: 0,
  },
  
  // ========== 任务物品 ==========
  {
    id: 'grandma_letter',
    name: '奶奶的信',
    description: '奶奶写给远方亲人的信。',
    category: 'quest',
    rarity: 'common',
    icon: '✉️',
    questItem: true,
    stackable: false,
    maxStack: 1,
    sellPrice: 0,
    buyPrice: 0,
  },
  {
    id: 'lost_toy',
    name: '丢失的玩具',
    description: '丹丹最喜欢的玩具。',
    category: 'quest',
    rarity: 'uncommon',
    icon: '🎾',
    questItem: true,
    stackable: false,
    maxStack: 1,
    sellPrice: 0,
    buyPrice: 0,
  },
  
  // ========== 特殊物品 ==========
  {
    id: 'ancient_guqin',
    name: '古琴',
    description: '一把古老的琴，琴弦上还残留着淡淡的光芒。',
    category: 'collectible',
    rarity: 'legendary',
    icon: '🎸',
    stackable: false,
    maxStack: 1,
    sellPrice: 1000,
    buyPrice: 0,
  },
  {
    id: 'dandan_favorite_food',
    name: '特制狗粮',
    description: '丹丹最爱吃的食物，用了特殊的配方。',
    category: 'consumable',
    rarity: 'rare',
    icon: '🥩',
    effects: [
      { type: 'buff', target: 'dandan_intimacy', value: 30, duration: 0 },
      { type: 'buff', target: 'dandan_health', value: 50, duration: 0 }
    ],
    stackable: true,
    maxStack: 10,
    sellPrice: 100,
    buyPrice: 200,
    consumable: true,
  },
  ...EXTENDED_ITEMS
];

// 背包系统
export class InventorySystem {
  private items: Map<string, InventoryItem> = new Map();
  private equipment: EquipmentSlots = {};
  private currency: number = 0;
  private capacity: number = 50;
  
  private onChange?: () => void;
  private onEquipChange?: () => void;
  
  constructor(capacity: number = 50) {
    this.capacity = capacity;
  }
  
  // 设置回调
  setCallbacks(callbacks: {
    onChange?: () => void;
    onEquipChange?: () => void;
  }): void {
    this.onChange = callbacks.onChange;
    this.onEquipChange = callbacks.onEquipChange;
  }
  
  // 获取物品定义
  getItemDef(itemId: string): Item | undefined {
    return ITEM_DATABASE.find(item => item.id === itemId);
  }
  
  // 添加物品
  addItem(itemId: string, quantity: number = 1): boolean {
    const itemDef = this.getItemDef(itemId);
    if (!itemDef) return false;
    
    // 检查背包容量
    if (!this.hasSpace(itemId, quantity)) {
      console.log('背包已满');
      return false;
    }
    
    const existing = this.items.get(itemId);
    
    if (existing) {
      // 已有物品，增加数量
      if (itemDef.stackable) {
        existing.quantity = Math.min(existing.quantity + quantity, itemDef.maxStack);
      } else {
        // 不可堆叠，创建新实例
        const newItem: InventoryItem = {
          itemId,
          quantity: 1,
        };
        this.items.set(`${itemId}_${Date.now()}`, newItem);
      }
    } else {
      // 新物品
      const newItem: InventoryItem = {
        itemId,
        quantity: itemDef.stackable ? quantity : 1,
      };
      this.items.set(itemId, newItem);
    }
    
    this.onChange?.();
    return true;
  }
  
  // 移除物品
  removeItem(itemId: string, quantity: number = 1): boolean {
    const existing = this.items.get(itemId);
    if (!existing || existing.quantity < quantity) {
      return false;
    }
    
    existing.quantity -= quantity;
    
    if (existing.quantity <= 0) {
      this.items.delete(itemId);
    }
    
    this.onChange?.();
    return true;
  }
  
  // 使用物品
  useItem(itemId: string): { success: boolean; effects?: ItemEffect[]; message?: string } {
    const itemDef = this.getItemDef(itemId);
    if (!itemDef) {
      return { success: false, message: '物品不存在' };
    }
    
    if (!itemDef.consumable || !itemDef.effects) {
      return { success: false, message: '该物品无法使用' };
    }
    
    const existing = this.items.get(itemId);
    if (!existing || existing.quantity <= 0) {
      return { success: false, message: '物品数量不足' };
    }
    
    // 使用物品
    this.removeItem(itemId, 1);
    
    return { success: true, effects: itemDef.effects };
  }
  
  // 装备物品
  equipItem(itemId: string): boolean {
    const itemDef = this.getItemDef(itemId);
    if (!itemDef || !itemDef.equipmentSlot) {
      return false;
    }
    
    const existing = this.items.get(itemId);
    if (!existing || existing.quantity <= 0) {
      return false;
    }
    
    // 卸下当前装备
    const slot = itemDef.equipmentSlot;
    if (this.equipment[slot]) {
      this.unequipItem(slot);
    }
    
    // 装备新物品
    this.equipment[slot] = existing;
    existing.equipped = true;
    
    this.onEquipChange?.();
    return true;
  }
  
  // 卸下装备
  unequipItem(slot: EquipmentSlot): boolean {
    const equipped = this.equipment[slot];
    if (!equipped) return false;
    
    equipped.equipped = false;
    delete this.equipment[slot];
    
    this.onEquipChange?.();
    return true;
  }
  
  // 获取装备属性总和
  getEquipmentStats(): { attack: number; defense: number; speed: number; charm: number; wisdom: number } {
    const stats = { attack: 0, defense: 0, speed: 0, charm: 0, wisdom: 0 };
    
    Object.values(this.equipment).forEach(item => {
      const itemDef = this.getItemDef(item.itemId);
      if (itemDef?.stats) {
        stats.attack += itemDef.stats.attack || 0;
        stats.defense += itemDef.stats.defense || 0;
        stats.speed += itemDef.stats.speed || 0;
        stats.charm += itemDef.stats.charm || 0;
        stats.wisdom += itemDef.stats.wisdom || 0;
      }
    });
    
    return stats;
  }
  
  // 检查是否有空间
  hasSpace(itemId: string, quantity: number = 1): boolean {
    const itemDef = this.getItemDef(itemId);
    if (!itemDef) return false;
    
    // 已有物品且可堆叠
    const existing = this.items.get(itemId);
    if (existing && itemDef.stackable) {
      return existing.quantity + quantity <= itemDef.maxStack;
    }
    
    // 检查总容量
    return this.items.size < this.capacity;
  }
  
  // 获取背包物品列表
  getItems(): InventoryItem[] {
    return Array.from(this.items.values());
  }
  
  // 获取特定类别物品
  getItemsByCategory(category: ItemCategory): InventoryItem[] {
    return this.getItems().filter(item => {
      const def = this.getItemDef(item.itemId);
      return def?.category === category;
    });
  }
  
  // 获取金币
  getCurrency(): number {
    return this.currency;
  }
  
  // 添加金币
  addCurrency(amount: number): void {
    this.currency += amount;
    this.onChange?.();
  }
  
  // 花费金币
  spendCurrency(amount: number): boolean {
    if (this.currency < amount) return false;
    this.currency -= amount;
    this.onChange?.();
    return true;
  }
  
  // 获取装备
  getEquipment(): EquipmentSlots {
    return { ...this.equipment };
  }
  
  // 获取背包使用率
  getCapacityUsage(): { current: number; max: number; percentage: number } {
    return {
      current: this.items.size,
      max: this.capacity,
      percentage: (this.items.size / this.capacity) * 100
    };
  }
  
  // 出售物品
  sellItem(itemId: string, quantity: number = 1): { success: boolean; gained: number } {
    const itemDef = this.getItemDef(itemId);
    if (!itemDef) return { success: false, gained: 0 };
    
    const existing = this.items.get(itemId);
    if (!existing || existing.quantity < quantity) {
      return { success: false, gained: 0 };
    }
    
    // 不能出售装备中的物品
    if (existing.equipped) {
      return { success: false, gained: 0 };
    }
    
    const gained = itemDef.sellPrice * quantity;
    this.removeItem(itemId, quantity);
    this.addCurrency(gained);
    
    return { success: true, gained };
  }
  
  // 导出数据
  export(): { items: InventoryItem[]; equipment: EquipmentSlots; currency: number } {
    return {
      items: this.getItems(),
      equipment: this.getEquipment(),
      currency: this.currency,
    };
  }
  
  // 导入数据
  import(data: { items: InventoryItem[]; equipment: EquipmentSlots; currency: number }): void {
    this.items.clear();
    data.items.forEach(item => {
      this.items.set(item.itemId, item);
    });
    this.equipment = data.equipment || {};
    this.currency = data.currency || 0;
    this.onChange?.();
  }
}